import { Component, input, output, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-drawer',
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <div 
        class="fixed inset-0 z-[9999]"
        [class]="backdropClasses"
        (click)="onBackdropClick()">
        <div
          [class]="drawerClasses"
          [attr.role]="'dialog'"
          [attr.aria-modal]="true"
          (click)="$event.stopPropagation()"
          (keydown.escape)="onEscape()">
          @if (showHeader()) {
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ header() }}</h2>
              @if (closable()) {
                <button
                  type="button"
                  (click)="close()"
                  class="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close drawer">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              }
            </div>
          }
          <div [class]="contentClasses">
            <ng-content></ng-content>
          </div>
        </div>
      </div>
    }
  `,
  styles: []
})
export class DrawerComponent {
  visible = input<boolean>(false);
  header = input<string>('');
  showHeader = input<boolean>(true);
  closable = input<boolean>(true);
  position = input<'left' | 'right' | 'top' | 'bottom'>('right');
  size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'>('md');
  dismissableMask = input<boolean>(true);
  blockScroll = input<boolean>(true);

  visibleChange = output<boolean>();
  closeEvent = output<void>();

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible() && this.closable()) {
      this.close();
    }
  }

  close(): void {
    this.visibleChange.emit(false);
    this.closeEvent.emit();
    if (this.blockScroll()) {
      document.body.style.overflow = '';
    }
  }

  onBackdropClick(): void {
    if (this.dismissableMask()) {
      this.close();
    }
  }

  get backdropClasses(): string {
    return 'bg-black/50 dark:bg-black/70 backdrop-blur-sm';
  }

  get drawerClasses(): string {
    const classes = [
      'absolute',
      'bg-white',
      'dark:bg-gray-800',
      'shadow-xl',
      'flex',
      'flex-col',
      'transition-transform',
      'duration-300',
      'ease-in-out'
    ];

    const sizeMap = {
      xs: 'w-64',
      sm: 'w-80',
      md: 'w-96',
      lg: 'w-[28rem]',
      xl: 'w-[32rem]',
      full: 'w-full'
    };

    const positionMap = {
      left: ['left-0', 'top-0', 'bottom-0', sizeMap[this.size()], 'rounded-r-lg'],
      right: ['right-0', 'top-0', 'bottom-0', sizeMap[this.size()], 'rounded-l-lg'],
      top: ['top-0', 'left-0', 'right-0', 'h-96', 'rounded-b-lg'],
      bottom: ['bottom-0', 'left-0', 'right-0', 'h-96', 'rounded-t-lg']
    };

    classes.push(...positionMap[this.position()]);

    return classes.join(' ');
  }

  get contentClasses(): string {
    return 'flex-1 overflow-y-auto px-6 py-4';
  }
}

